/**
 * Always call out use of strict mode
 * ~ see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Strict_mode
 * */
'use strict';

/**
 * We will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

/**
 * We will reference a global object that we may use to fetch preset properties
 */
window.RequestInfo = window.RequestInfo || {};


/**Use the Modernizr.js library to determine whether the browser used by the client
 * is compatible with our application, based on the core JS, HTML, and CSS features we employ.
 * ~ see https://modernizr.com/docs
 *
 * To build an updated Modernizr configuration run the npm script;
 *  npm run modernizr-build
 * Which makes use of the ./modernizr-config.json file for setup
 * ~ all available options may be viewed in the example full config file at
 *  ./node_modules/modernizr/lib/config-all.json
 */
require('./modernizr.js');


/**
 * Next, we include the Vue templating engine (version 2.*)
 */
window.Vue = require('vue');


/**
 * We will include Sentry for Error tracking
 * ~see https://docs.sentry.io/error-reporting/quickstart/?platform=node
 *
 * We'll also add a couple additional Sentry js report directives
 * that we will fetch through objects populated by a prior script run on the main app page (app.blade.php)
 * that will populate a RequestInfo object
 * ~ see https://docs.sentry.io/error-reporting/configuration/?platform=browser
 * ~ see https://docs.sentry.io/enriching-error-data/context/?platform=javascript
 */
window.Sentry = require('@sentry/browser');
let Integrations = require('@sentry/integrations');
window.Sentry.init({
    /* eslint-disable no-undef */
    dsn: Globals.SENTRY_LARAVEL_DSN,
    debug: Globals.NODE_ENV !== 'production',
    /* eslint-enable no-undef */

    release: window.RequestInfo.environment.app_release,
    environment: window.RequestInfo.environment.app_env,
    attachStacktrace: false,
    // shutdownTimeout: 5,  // deprecated
    integrations: [new Integrations.Vue({ 'Vue': window.Vue, attachProps: true, logErrors: true })],
    beforeSend(event, hint) {
        const error = hint.originalException;
        console.warn(error);

        // ~see https://docs.sentry.io/error-reporting/configuration/filtering/?platform=node
        // We only want Sentry to report Javascript and Vue error's in a production or staging environment
        if (
            (window.RequestInfo.environment.app_env === 'production' || window.RequestInfo.environment.app_env === 'staging')
        ){

            // Show the feedback dialog
            // ~ see https://docs.sentry.io/enriching-error-data/user-feedback/?platform=browser#collecting-feedback
            window.Sentry.showReportDialog({ eventId: event.event_id });
            return event;
        }
    }
});

window.Sentry.configureScope((scope) => {
    scope.setUser({
        'email': window.RequestInfo.user.email
    });

    /* eslint-disable no-undef */
    scope.setTag('project_title', `${Globals.APP_NAME} (Client)`);
    scope.setTag('version', window.RequestInfo.environment.app_version);
    /* eslint-enable no-undef */

    scope.setLevel(window.Sentry.Severity.Error);
});


/* Report issues and messages to Sentry if we have 'extended_error_reporting' enabled */
window.Sentry.sentryClientReport = function(message = 'Something to report from the Client', extra = {}){
    window.Sentry.withScope((scope) => {
        scope.setExtra('extra', extra);
        scope.setLevel(window.Sentry.Severity.Info);
        window.Sentry.captureMessage(message);
    });
};



/* We will parse out the site_settings object now that Sentry is initialized and report if there are issues */
try {
    window.RequestInfo.site_settings = JSON.parse(window.RequestInfo.site_settings);
}
catch (exception){
    window.Sentry.captureException(exception);
}

/**
 * Load Lodash
 * ~ see https://lodash.com/docs/
 */
window._ = require('lodash');


/**
 * Load Luxon Libraries
 * ~ Formats: https://moment.github.io/luxon/docs/manual/formatting.html#table-of-tokens
 * ~ DateTime: see https://moment.github.io/luxon/docs/class/src/datetime.js~DateTime.html
 * ~ Interval: see https://moment.github.io/luxon/docs/class/src/interval.js~Interval.html
 * ~ Duration: see https://moment.github.io/luxon/docs/class/src/duration.js~Duration.html
 */
const { DateTime, Interval, Duration } = require('luxon');
window.DateTime = DateTime;
window.Interval = Interval;
window.Duration = Duration;


/**
 * We'll load jQuery and the Bootstrap jQuery plugin which provides support
 * for JavaScript based Bootstrap features such as modals and tabs. This
 * code may be modified to fit the specific needs of your application.
 */
try {
    // We use bootstrap.bundle (vs default of bootstrap/dist/js/bootstrap.js) since it contains the Popper.js library, which we need for pop-overs
    window.$ = require('jquery');
    require('bootstrap/dist/js/bootstrap.bundle');
} catch (e) {}


/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 *
 * Next we will register the CSRF Token as a common header with Axios so that
 * all outgoing HTTP requests automatically have it attached. This is just
 * a simple convenience so we don't have to attach every token manually.
 */
window.axios = require('axios');
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

let token = document.head.querySelector('meta[name="csrf-token"]');
if (token) {
    window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content;
}
else {
    console.error('CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token');
}


/**
 * Load full Font Awesome (Free version) js dependencies
 * ~ see https://fontawesome.com/how-to-use/on-the-web/referencing-icons/basic-use
 */
require('@fortawesome/fontawesome-free/js/all');


/**
 * We now pull in any globally required custom scripts
 */
